import { PERMISSIONS } from '@ibe/constants';
import { ErrorState, LoadingState } from '@ibe/ui';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { NotPermitted, PermissionGuard } from '../../../auth/permission-guard.js';
import { FormPage } from '../../../components/form-page/form-page.js';
import { errorTextFor } from '../../../lib/api/error-message.js';
import { useAppForm } from '../../../lib/forms/use-app-form.js';
import { useCreateTenantAdmin, useUpdateTenantAdmin } from '../api/mutations.js';
import { useTenantAdmin, useTenantOptions } from '../api/queries.js';
import {
  tenantAdminFormInput,
  type TenantAdminDetail,
  type TenantAdminFormInput,
} from '../api/schemas.js';
import { TenantAdminFields } from '../forms/tenant-admin-fields.js';

/**
 * Creating and editing a tenant administrator.
 *
 * One component for both, because they are the same fields under the same
 * validation; only the defaults, the title and the mutation differ. Splitting
 * them would mean two files that have to be kept in step by hand. Which one
 * this is comes from the route, not a prop, so the URL is the truth.
 *
 * There is no error handling here, and that is the point. `useAppForm` owns
 * schema validation, server field errors attached to their fields, everything
 * else in the feedback dialog, the success message, the double-submit guard
 * and the logging.
 *
 * Loading and error states live in the shell components; the form components
 * only mount once data is ready, so every hook is called in the same order on
 * every render.
 */

const CREATE_DEFAULTS: TenantAdminFormInput = {
  tenantId: '',
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  status: 'ACTIVE',
};

/** Null columns become empty strings: a controlled input cannot take null. */
function defaultsFrom(admin: TenantAdminDetail): TenantAdminFormInput {
  return {
    tenantId: admin.tenantId,
    email: admin.email,
    firstName: admin.firstName ?? '',
    lastName: admin.lastName ?? '',
    status: admin.status,
  };
}

export function TenantAdminFormPage(): React.JSX.Element {
  const userId = useParams().userId;
  const isEdit = userId !== undefined;

  return (
    <PermissionGuard permission={PERMISSIONS.TENANT_USER_MANAGE} fallback={<NotPermitted />}>
      {isEdit ? <EditTenantAdmin userId={userId} /> : <CreateTenantAdmin />}
    </PermissionGuard>
  );
}

function CreateTenantAdmin(): React.JSX.Element {
  const [params] = useSearchParams();
  const options = useTenantOptions();

  if (options.isPending) {
    return <LoadingState label="Loading tenants" />;
  }

  if (options.error !== null) {
    return (
      <ErrorState
        description={errorTextFor(options.error) ?? 'The tenant list could not be loaded.'}
      />
    );
  }

  const tenantOptions = options.data;
  const tenantId = params.get('tenantId') ?? tenantOptions[0]?.id ?? '';

  return <CreateTenantAdminForm tenantOptions={tenantOptions} tenantId={tenantId} />;
}

interface CreateTenantAdminFormProps {
  readonly tenantOptions: readonly { readonly id: string; readonly name: string }[];
  readonly tenantId: string;
}

function CreateTenantAdminForm({
  tenantOptions,
  tenantId,
}: CreateTenantAdminFormProps): React.JSX.Element {
  const navigate = useNavigate();
  const create = useCreateTenantAdmin();

  const form = useAppForm<TenantAdminFormInput>({
    schema: tenantAdminFormInput,
    defaultValues: { ...CREATE_DEFAULTS, tenantId },
    successMessage: 'Tenant administrator created',
    onSubmit: async (values) => {
      const created = await create.mutateAsync(values);
      void navigate(`/tenant-admins?tenantId=${encodeURIComponent(created.tenantId)}`);
    },
  });

  return (
    <FormPage
      title="New tenant administrator"
      description="Create an owner for a tenant. They will be emailed their sign-in details."
      backTo={
        tenantId === ''
          ? '/tenant-admins'
          : `/tenant-admins?tenantId=${encodeURIComponent(tenantId)}`
      }
      backLabel="Administrators"
      submitLabel="Create administrator"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <TenantAdminFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
        setValue={form.setValue}
        mode="create"
        tenantOptions={tenantOptions}
      />
    </FormPage>
  );
}

interface EditTenantAdminProps {
  readonly userId: string;
}

function EditTenantAdmin({ userId }: EditTenantAdminProps): React.JSX.Element {
  const [params] = useSearchParams();
  const tenantId = params.get('tenantId') ?? '';
  const query = useTenantAdmin(userId, tenantId);
  const options = useTenantOptions();

  if (tenantId === '') {
    return <ErrorState description="No tenant was selected for this administrator." />;
  }

  if (query.isPending || options.isPending) {
    return <LoadingState label="Loading administrator" />;
  }

  if (query.error !== null) {
    return (
      <ErrorState
        description={errorTextFor(query.error) ?? 'This administrator could not be loaded.'}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  if (options.error !== null) {
    return (
      <ErrorState
        description={errorTextFor(options.error) ?? 'The tenant list could not be loaded.'}
      />
    );
  }

  return <EditTenantAdminForm userId={userId} admin={query.data} tenantOptions={options.data} />;
}

interface EditTenantAdminFormProps {
  readonly userId: string;
  readonly admin: TenantAdminDetail;
  readonly tenantOptions: readonly { readonly id: string; readonly name: string }[];
}

function EditTenantAdminForm({
  userId,
  admin,
  tenantOptions,
}: EditTenantAdminFormProps): React.JSX.Element {
  const navigate = useNavigate();
  const update = useUpdateTenantAdmin();

  const form = useAppForm<TenantAdminFormInput>({
    schema: tenantAdminFormInput,
    defaultValues: defaultsFrom(admin),
    successMessage: 'Tenant administrator updated',
    onSubmit: async (values) => {
      await update.mutateAsync({ userId, values });
      void navigate(`/tenant-admins?tenantId=${encodeURIComponent(admin.tenantId)}`);
    },
  });

  return (
    <FormPage
      title={admin.displayName}
      description="Update the administrator's name and status."
      backTo={`/tenant-admins?tenantId=${encodeURIComponent(admin.tenantId)}`}
      backLabel="Administrators"
      submitLabel="Save changes"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <TenantAdminFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
        setValue={form.setValue}
        mode="edit"
        tenantOptions={tenantOptions}
      />
    </FormPage>
  );
}
